import api from "../services/api";
import { $Q, $Qll } from "../utils/query-selector";
import { stringToHTML } from "../utils/to-html";
import { dataToggle } from "../utils/toggle-dataset";
import onClickOutsideNode from "../utils/onclick-outside";
import { toggleSubscriptionInfo, toggleSubscriptionOptions } from "./product-subscriptions";
import { btnAddToCart } from "./cart";
import { initBundleOptions, initKlaviyoBtn } from "./jolly-option-toggle";
import { mainProductSwiperModal } from "../components/product-slider";

/**
 * It listen load and resize events to assign the respective class to cart
 */
export const onLoadAndResizeProductCard = () => {
  const productCards = $Qll('.js-product-card');

  if (!productCards) return;

  window.addEventListener('load', () => assignModalCartClass());
  window.addEventListener('resize', () => assignModalCartClass());
} 

/**
 * It assign the class based on window size
 */
const assignModalCartClass = () => {
  const modalCart = $Q('.modal-cart');

  if (!modalCart) return;

  if (window.innerWidth >= 768) {
    modalCart.classList.remove('modal-cart--mobile');
  } else {
    modalCart.classList.add('modal-cart--mobile');
  }
}

const modalCloseClick = () => {
  const modalCloseBtn = $Q('.modal-cart-close');

  if (!modalCloseBtn) return;

  modalCloseBtn.addEventListener('click', ()=> {
    checkModalCart();
  })
}

/**
 * It reads the click on product card
 * @param {boolean} update - If it is from update
 */
export const onClickProductCard = (update) => {
  const productCards = $Qll('.js-product-card');

  if (!productCards) return;

  const assignedEventCards = calculateProductCardEvent();

  productCards.forEach((card, index) => {
    if (update && (index <= assignedEventCards - 1)) return;

    const link = $Q('.product-card__link', card);


    if (!link) return;

    link.addEventListener('click', async (e) => {
      e.preventDefault();

      const modalWrapper = $Q('#modal-wrapper');
      const modalCart = $Q('.modal-cart');
      const modalLoader = $Q('#modal-loader');
      if (!modalCart || !modalLoader || !modalWrapper) return;

      dataToggle(modalWrapper, true);
      dataToggle(modalLoader);

      const isModalSet = await setModalCartContent(card, modalCart, modalLoader);
      onClickImageOrTitle(link, isModalSet);
    })
  })
}

/**
 * It calculates the number of product cards which were previously on the DOM
 * in order to not reassign the click event over them
 * @returns {number} assignedEventCards - It is the number of previously
 * events assigned on product cards
 */
const calculateProductCardEvent = () => {
  const collectionGrid = $Q('#product-grid');

  if (!collectionGrid) return;

  const pagination = collectionGrid.dataset.pagination;
  const currentPage = collectionGrid.dataset.current;

  if (!pagination || !currentPage) return;

  const assignedEventCards = parseInt(pagination) * parseInt(currentPage - 1);
  return assignedEventCards;
}

/**
 * It sets the modal cart content fetching the section and replacing in the DOM
 * @param {HTMLElement} card - It is the clicked card element
 * @param {HTMLElement} modalCart - It is the modal cart element
 * @param {HTMLElement} modalLoader - It is the modal loader element
 */
const setModalCartContent = async (card, modalCart, modalLoader) => {
  const cardAnchor = $Q('.product-card__head', card);

  const modalCartInner = $Q('.modal-cart-inner', modalCart);

  if (!cardAnchor) return;

  const productUrl = cardAnchor.getAttribute('href');
  const variantId = cardAnchor.dataset.variant;
  const sectionFetched = await fetchModalProduct(variantId, productUrl);
  modalCartInner.innerHTML = sectionFetched.innerHTML;

  assignLabelsValues(modalCart);

  if (modalLoader.dataset.active === 'true') dataToggle(modalLoader);
  if (modalCart.dataset.active === 'false') dataToggle(modalCart);

  onClickOutsideNode('.modal-cart', null, 'modal-cart');
  modalCloseClick();
  triggerReadModalProduct();

  setTimeout(() => {
    const variantOption = $Q('.modal-cart .jc-variant-option.active');

    if (variantOption) {
      variantOption.click();
    }

  }, 300)

  return sectionFetched ? true : false
}

/**
 * This function assign label values from settings after rendering
 * @param {HTMLElement} modalCart - It is the parent element as a reference
 */
const assignLabelsValues = (modalCart) => {
  const singleLabel = $Q('label[for="single"]', modalCart);
  const subscriptionLabel = $Q('label[for="subscribe"]', modalCart);

  if (!singleLabel || !subscriptionLabel) return;

  singleLabel.innerText = modalCart.dataset.single;
  subscriptionLabel.innerText = modalCart.dataset.subscription;
  createModalSubscriptionLink(modalCart);
}

/**
 * This function creates the link for subscription info
 * @param {HTMLElement} modalCart - It is the parent element as a reference
 */
const createModalSubscriptionLink = (modalCart) => {
  const subscriptionInfo = $Q('.main-product__subscriptions-about', modalCart);

  if (!subscriptionInfo) return;

  const anchor = document.createElement('a');
  anchor.classList.add('main-product__subscriptions-about-link');
  anchor.innerText = modalCart.dataset.text;
  anchor.setAttribute('href', modalCart.dataset.url);
  subscriptionInfo.appendChild(anchor);
}

/**
 * Fetch product information through section rendering api
 * @param {number} variant - It is the variant id
 * @param {String} productUrl - It is the clicked product url
 * @returns {HTMLElement} - It is the section html response
 */ 
export const fetchModalProduct = async (variant, productUrl) => {
  const params = {
    variant,
    sections: 'ajax-modal-product'
  }

  const response = await api.shopifySectionByUrlAndParams(productUrl, params);
  const sectionHTML = stringToHTML(response['ajax-modal-product']);

  return sectionHTML;
}

/**
 * It sets the functions in order to read the buttons in modal cart
 */
const triggerReadModalProduct = () => {
  btnAddToCart('.modal-cart .add-product-cart');
  toggleSubscriptionOptions('.modal-cart .main-product__subscriptions-wrapper', null, true);
  toggleSubscriptionInfo('.modal-cart ');
  initBundleOptions('.modal-cart ');
  initKlaviyoBtn('.modal-cart ');
  mainProductSwiperModal('.modal-cart ', 0, true);
}

/**
 * It checks if there is a modal cart
 */
export const checkModalCart = () => {
  removeWrapperOverlay();
  hideModalCart();
}

/**
 * It removes the cart wrapper overtlay
 */
const removeWrapperOverlay = () => {
  const wrapperOverlay = $Q('#overlay--modal-wrapper');

  if (!wrapperOverlay) return;

  wrapperOverlay.remove();
}

/**
 * It hides the modal cart
 */
const hideModalCart = () => {
  const modalCart = $Q('.modal-cart');
  const modalLoader = $Q('#modal-loader');
  const modalWrapper = $Q('#modal-wrapper');

  if (!modalCart || !modalLoader) return;

  if (modalCart.dataset.active === 'true') dataToggle(modalCart);
  if (modalLoader.dataset.active === 'true') dataToggle(modalLoader);
  if (modalWrapper.dataset.active === 'true') dataToggle(modalWrapper);

}

/**
 * This function reads click event on title or image for modal cart
 * @param {link} link - This is the url for the product
 */
const onClickImageOrTitle = (link) => {
  const title = $Q('.modal-cart .main-product__title');
 // const img = $Q('.modal-cart .main-product__image');

  if (title) {
    title.addEventListener('click', () => {
      let mainProduct = $Q('.modal-cart .main-product__detail');
      
      if (!mainProduct) return;
      let link = mainProduct.dataset.url;
      window.location.href = link;
  });
} 

 // if (img) img.addEventListener('click', () => openProductUrl(link));
}

/**
 * This opens a new url for the product page
 * @param {String} link - This is the url for the product
 */
// const openProductUrl = (link) => {
//   window.location.assign(link);
// }


